import React, { useState, useEffect } from 'react';
import { ModelType, IAudioGeneration, PaginationResult, AudioGenerationQuery, PaginationQuery, AudioGenerationStatus } from '@packages/global';
import { audioGenerationApi } from '../services/user/audioGeneration.api';
import { useModels } from '../hooks/useModels';
import Pagination from '../components/Pagination';
import AudioGenerationCard from '../components/AudioGeneration/AudioGenerationCard';
import SearchBar from '../components/AudioGeneration/SearchBar';
import AudioGenerationForm from '../components/AudioGeneration/AudioGenerationForm';
import { useDialog } from '../hooks/useDialog';
import { useTranslation } from 'react-i18next';
import { useWebSocketContext } from '../services/websocket/WebSocketContext';
import { MessageType, TaskStatusChangedMessage, TaskType, TaskStatus } from '../services/websocket/types';
import '../styles/line-clamp.css';


interface AudioGenerationFormData {
  prompt: string;
  spkAudioUrl: string;
  emoAudioUrl?: string;
  emoAlpha?: number;
  emoText?: string;
  useRandom?: boolean;
  maxTextTokensPerSegment?: number;
  matchReferenceDuration?: boolean;
  modelId: string;
}

export default function AudioGenerationsPage() {
  const [items, setItems] = useState<IAudioGeneration[]>([]);
  const { models, loading: modelsLoading } = useModels(ModelType.TEXT_TO_SPEECH);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState<IAudioGeneration | null>(null);
  const [currentPlayingAudio, setCurrentPlayingAudio] = useState<HTMLAudioElement | null>(null);
  const { alert, confirm } = useDialog();
  const { t } = useTranslation(['generation', 'common']);
  const { client, isConnected } = useWebSocketContext();
  const [formData, setFormData] = useState<AudioGenerationFormData>({
    prompt: '',
    spkAudioUrl: '',
    emoAudioUrl: '',
    emoAlpha: 0.5,
    emoText: '',
    useRandom: false,
    maxTextTokensPerSegment: 200,
    matchReferenceDuration: false,
    modelId: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<AudioGenerationStatus | ''>('');
  const [pagination, setPagination] = useState<PaginationResult>({
    page: 1,
    limit: 24,
    total: 0,
    totalPages: 0,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  const loadItems = async (
    queryParams: Partial<AudioGenerationQuery> = {},
    page: number = 1,
    limit: number = 24
  ) => {
    try {
      const queryData: PaginationQuery<AudioGenerationQuery> = {
        query: {
          search: queryParams.search ?? searchTerm,
          status: queryParams.status ?? (statusFilter || undefined),
          ...queryParams
        },
        pagination: {
          page,
          limit,
          sortBy: 'createdAt',
          sortOrder: 'desc'
        }
      };
      const response = await audioGenerationApi.audioGenerationPageList(queryData);
      setItems(response.items);
      setPagination((prev) => ({
        ...prev,
        page,
        limit,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages
      }));
    } catch (error) {
      console.error('加载音频生成任务失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  // 🔥 页面级别订阅 WebSocket，监听所有音频生成任务的状态更新
  useEffect(() => {
    if (!client || !isConnected) {
      return;
    }

    // 监听状态变化消息
    const unsubscribeStatusChanged = client.onMessage(
      MessageType.TASK_STATUS_CHANGED,
      (message: TaskStatusChangedMessage) => {
        if (message.taskType !== TaskType.AUDIO_GENERATION) return;

        const statusMap: Record<TaskStatus, AudioGenerationStatus> = {
          [TaskStatus.PENDING]: AudioGenerationStatus.PENDING,
          [TaskStatus.SUBMITTED]: AudioGenerationStatus.PENDING,
          [TaskStatus.PROCESSING]: AudioGenerationStatus.PROCESSING,
          [TaskStatus.COMPLETED]: AudioGenerationStatus.COMPLETED,
          [TaskStatus.FAILED]: AudioGenerationStatus.FAILED
        };

        const newStatus = statusMap[message.newStatus];
        if (!newStatus) return;

        setItems(prevItems => {
          const itemIndex = prevItems.findIndex(item => item.id === message.taskId);
          if (itemIndex === -1) return prevItems;

          const newItems = [...prevItems];
          newItems[itemIndex] = { ...newItems[itemIndex], status: newStatus };

          if (newStatus === AudioGenerationStatus.COMPLETED || newStatus === AudioGenerationStatus.FAILED) {
            audioGenerationApi.getAudioGeneration(message.taskId)
              .then(updatedItem => {
                setItems(prev => prev.map(item => item.id === updatedItem.id ? updatedItem : item));
              })
              .catch(console.error);
          }

          return newItems;
        });
      }
    );

    const unsubscribeCompleted = client.onMessage(MessageType.TASK_COMPLETED, (message: any) => {
      if (message.taskType !== TaskType.AUDIO_GENERATION) return;
      audioGenerationApi.getAudioGeneration(message.taskId)
        .then(updatedItem => setItems(prev => prev.map(item => item.id === updatedItem.id ? updatedItem : item)))
        .catch(console.error);
    });

    const unsubscribeFailed = client.onMessage(MessageType.TASK_FAILED, (message: any) => {
      if (message.taskType !== TaskType.AUDIO_GENERATION) return;
      audioGenerationApi.getAudioGeneration(message.taskId)
        .then(updatedItem => setItems(prev => prev.map(item => item.id === updatedItem.id ? updatedItem : item)))
        .catch(console.error);
    });

    return () => {
      unsubscribeStatusChanged();
      unsubscribeCompleted();
      unsubscribeFailed();
    };
  }, [client, isConnected]);

  // 当模型加载完成且表单中没有选择模型时，自动选择第一个模型
  useEffect(() => {
    if (!modelsLoading && !formData.modelId && models.length > 0) {
      setFormData(prev => ({ ...prev, modelId: models[0].id }));
    }
  }, [models, modelsLoading, formData.modelId]);

  const handleSearchTermChange = (term: string) => {
    setSearchTerm(term);
    loadItems({ search: term, status: statusFilter || undefined }, 1);
  };

  const handleStatusFilterChange = (status: AudioGenerationStatus | '') => {
    setStatusFilter(status);
    loadItems({ search: searchTerm, status: status || undefined }, 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.modelId) {
      await alert(t('audio.form.selectModel'), t('common:warning'), 'warning');
      return;
    }

    try {
      // 编辑时也是创建新任务，而不是更新
      const newTask = await audioGenerationApi.createAudioGeneration(formData);
      // 🔥 直接把新任务加入列表头部
      setItems(prevItems => [newTask, ...prevItems]);
      setPagination(prev => ({ ...prev, total: prev.total + 1 }));
      handleCloseForm();
    } catch (error) {
      console.error('操作失败:', error);
    }
  };

  const handleItemUpdate = (updatedItem: IAudioGeneration) => {
    setItems(prevItems =>
      prevItems.map(item =>
        item.id === updatedItem.id ? updatedItem : item
      )
    );
  };

  const handleEdit = (item: IAudioGeneration) => {
    setEditingItem(item);
    setFormData({
      prompt: item.prompt,
      spkAudioUrl: item.spkAudioUrl,
      emoAudioUrl: item.emoAudioUrl,
      emoAlpha: item.emoAlpha,
      emoText: item.emoText,
      useRandom: item.useRandom,
      maxTextTokensPerSegment: item.maxTextTokensPerSegment,
      matchReferenceDuration: item.matchReferenceDuration,
      modelId: item.modelId
    });
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm(t('audio.deleteConfirm'), t('common:confirmDelete'), 'danger');
    if (confirmed) {
      try {
        await audioGenerationApi.deleteAudioGeneration(id);
        loadItems();
      } catch (error) {
        console.error('删除失败:', error);
      }
    }
  };

  const handleCloseForm = () => {
    setEditingItem(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      prompt: '',
      spkAudioUrl: '',
      emoAudioUrl: '',
      emoAlpha: 0.5,
      emoText: '',
      useRandom: false,
      maxTextTokensPerSegment: 200,
      matchReferenceDuration: false,
      modelId: models.length > 0 ? models[0].id : ''
    });
  };

  const handleFormDataChange = (data: Partial<AudioGenerationFormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  const handleAudioPlay = (audioElement: HTMLAudioElement) => {
    // 如果有其他音频正在播放，暂停它
    if (currentPlayingAudio && currentPlayingAudio !== audioElement) {
      currentPlayingAudio.pause();
    }
    setCurrentPlayingAudio(audioElement);
  };

  return (
    <div className="flex h-screen bg-bg-page">
      {/* 左侧表单 */}
      <AudioGenerationForm
        editingItem={editingItem}
        formData={formData}
        models={models}
        modelsLoading={modelsLoading}
        onSubmit={handleSubmit}
        onFormDataChange={handleFormDataChange}
        onReset={handleCloseForm}
      />

      {/* 右侧内容区域 */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {/* 页面标题 */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-50">{t('audio.title')}</h1>
              <p className="mt-1 text-sm text-gray-400">{t('audio.subtitle')}</p>
            </div>
            <SearchBar
              searchTerm={searchTerm}
              statusFilter={statusFilter}
              onSearchTermChange={handleSearchTermChange}
              onStatusFilterChange={handleStatusFilterChange}
            />
          </div>

          {/* 卡片列表 */}
          {loading ? (
            <div className="bg-bg-surface rounded-xl border border-border-subtle shadow-sm p-12">
              <div className="flex justify-center items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-3 text-gray-400">{t('common:loading')}</span>
              </div>
            </div>
          ) : items.length === 0 ? (
            <div className="bg-bg-surface rounded-xl border border-border-subtle shadow-sm p-12">
              <div className="text-center">
                <p className="text-gray-400">{t('audio.empty')}</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,240px)] justify-between gap-x-4 gap-y-6">
              {items.map((item) => (
                <AudioGenerationCard
                  key={item.id}
                  item={item}
                  models={models}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onUpdate={handleItemUpdate}
                  onAudioPlay={handleAudioPlay}
                />
              ))}
            </div>
          )}

          {/* 分页组件 */}
          {pagination.totalPages > 1 && (
            <div className="bg-bg-surface rounded-xl border border-border-subtle shadow-sm p-4 mt-6">
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={(page) => loadItems({}, page, pagination.limit)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}